import React, { useEffect, useState } from "react";
import Edit from "../../../asset/svgs/Edit";
import Key from "../../../asset/svgs/Key";
import ArrowLeft from "../../../asset/svgs/ArrowLeft";
import "./Styles/CompanyProfile.css";

import { Avatar, message, Spin } from "antd";
import { useChangePassword } from "../hooks/useAuth";
import { useGetUser, useUpdateUser } from "../hooks/useUser";
import { NeonHexagonLoader } from "../Components/JobLoader";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";

const ProfileManagement = () => {
  const [currentScreen, setCurrentScreen] = useState("profile");
  const { data: user, loading, getCurrentUser } = useGetUser();
  const userId = localStorage.getItem("jobUserId");
  const [companyData, setCompanyData] = useState({
    name: "",
    email: "",
    avatar: null, // This will be the URL from backend
  });

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUser = async () => {
      await getCurrentUser(userId);
    };

    fetchUser();
  }, [getCurrentUser, userId]);

  // Update company data when user data is available
  useEffect(() => {
    if (user) {
      setCompanyData({
        name: user.company_name || "",
        email: user.email || "",
        avatar: user.company_logo || "", // This is the URL
      });
    }
  }, [user]);

  const CompanyHeader = ({ changeImage }) => (
    <div className="company-info-header">
      <span className="company-logo">
        <Avatar
          size={96}
          src={companyData?.avatar}
          style={{
            backgroundColor: "#f5f5f5",
            borderRadius: "2rem",
          }}
        >
          {companyData?.name?.charAt(0)?.toUpperCase() || "A"}
        </Avatar>
        {changeImage && (
          <>
            <button
              className="change-image-link"
              onClick={() => setCurrentScreen("edit")}
            >
              Change Image
            </button>
          </>
        )}
      </span>
      <div className="company-card-info">
        <p className="company-name-text">{companyData?.name}</p>
        <p className="company-email">{companyData?.email}</p>
      </div>
    </div>
  );

  // Company Profile Component
  const CompanyProfile = () => (
    <div className="screen-container">
      <div className="company-profile-header">
        <h1 className="header-title">Company Profile</h1>
      </div>

      <div className="content">
        <div className="company-info">
          <CompanyHeader changeImage={false} />
        </div>

        <div className="company-button-group">
          <button
            className="btn btn-primary"
            onClick={() => setCurrentScreen("edit")}
          >
            <Edit size={18} />
            <span>Edit Profile</span>
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => setCurrentScreen("password")}
          >
            <Key size={18} />
            <span>Change Password</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Edit Profile Component
  const EditProfile = () => {
    const { updateUserProfile, loading } = useUpdateUser();

    // Separate state for form data and file
    const [formData, setFormData] = useState({
      name: companyData.name,
      email: companyData.email,
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(companyData.avatar);

    // Update form data when companyData changes
    useEffect(() => {
      setFormData({
        name: companyData.name,
        email: companyData.email,
      });
      setPreviewUrl(companyData.avatar);
    }, []);

    // Handle file selection
    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedFile(file);

        // Create preview URL for the selected file
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      const submitFormData = new FormData();
      submitFormData.append("company_name", formData.name);
      submitFormData.append("email", formData.email);

      // Only append file if a new one was selected
      if (selectedFile) {
        submitFormData.append("company_logo", selectedFile);
      }

      try {
        await updateUserProfile(userId, submitFormData);
        message.success("Profile updated successfully!");

        // Update the main companyData state with new values
        setCompanyData({
          ...companyData,
          name: formData.name,
          email: formData.email,
          avatar: previewUrl, // Update with the new preview
        });

        setCurrentScreen("profile");
      } catch (error) {
        message.error("Couldn't save profile. Please try again.");
        console.error("Update error:", error);
      }
    };

    return (
      <div className="screen-container">
        <div className="header-with-back">
          <button
            onClick={() => setCurrentScreen("profile")}
            className="back-button"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="header-title">Edit Profile</h1>
        </div>

        <div className="content">
          {/* Show updated avatar preview */}
          <div className="company-info-header">
            <span className="company-logo">
              <Avatar
                size={96}
                src={previewUrl}
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "2rem",
                }}
              >
                {formData?.name?.charAt(0)?.toUpperCase() || "A"}
              </Avatar>
            </span>
            <div className="company-card-info">
              <p className="company-name-text">{formData?.name}</p>
              <p className="company-email">{formData?.email}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Company Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="form-input"
                placeholder="Enter company name"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="form-input"
                placeholder="Enter email address"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Change Company Logo</label>
              <input
                type="file"
                onChange={handleFileChange}
                className="form-input"
                accept="image/*"
              />
              {selectedFile && (
                <p className="file-info">Selected: {selectedFile.name}</p>
              )}
            </div>

            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? <Spin /> : "Save"}
            </button>
          </form>
        </div>
      </div>
    );
  };

  // Change Password Component
  const ChangePassword = () => {
    const { changePassword, loading: passwordLoading } = useChangePassword();
    const [showPassword, setShowPassword] = useState(false);
    const [passwords, setPasswords] = useState({
      current: "",
      new: "",
      confirm: "",
    });
    const [passwordError, setPasswordError] = useState(null);

    const handlePasswordChange = (field, value) => {
      setPasswords({ ...passwords, [field]: value });
      // Clear error when user starts typing
      if (passwordError) {
        setPasswordError(null);
      }
    };

    // Password validation function
    const validatePassword = (password) => {
      if (password.length < 8) {
        return "Password must be at least 8 characters long.";
      }

      const regexUppercase = /[A-Z]/;
      const regexLowercase = /[a-z]/;
      const regexNumber = /[0-9]/;
      const regexSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;

      if (!regexUppercase.test(password))
        return "Password must contain at least one uppercase letter.";
      if (!regexLowercase.test(password))
        return "Password must contain at least one lowercase letter.";
      if (!regexNumber.test(password))
        return "Password must contain at least one number.";
      if (!regexSpecialChar.test(password))
        return "Password must contain at least one special character.";

      return null; // Return null if all validations pass
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      // Check if all fields are filled
      if (!passwords.current || !passwords.new || !passwords.confirm) {
        setPasswordError("All fields are required.");
        return;
      }

      if (passwords.new !== passwords.confirm) {
        setPasswordError("New passwords do not match!");
        return;
      }

      // Validate new password
      const validationError = validatePassword(passwords.new);
      if (validationError) {
        setPasswordError(validationError);
        return;
      }

      setPasswordError(null); // Clear previous errors

      try {
        await changePassword(userId, {
          old_password: passwords.current,
          new_password: passwords.new,
        });

        message.success("Password changed successfully!");

        // Reset form
        setPasswords({
          current: "",
          new: "",
          confirm: "",
        });

        setCurrentScreen("profile");
      } catch (error) {
        setPasswordError(
          "Failed to change password. Please check your current password."
        );
        console.error("Password change error:", error);
      }
    };

    return (
      <div className="screen-container">
        <div className="header-with-back">
          <button
            onClick={() => setCurrentScreen("profile")}
            className="back-button"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="header-title">Change Password</h1>
        </div>

        <div className="content">
          <div style={{ marginBottom: "1.5rem" }}>
            <CompanyHeader changeImage={false} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type={showPassword ? "text" : "password"}
                value={passwords.current}
                onChange={(e) =>
                  handlePasswordChange("current", e.target.value)
                }
                className="form-input form-input-gray"
                placeholder="Enter current password"
                required
              />
              <span
                className="job-toggle-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </span>
              <Link
                to="/jobs/job-auth/reset-password"
                style={{ textDecoration: "none" }}
                className="job-forgot-password"
              >
                Forgot Password?
              </Link>
              {/* <button
                type="button"
                className="forgot-password"
                onClick={() => {
                  // Handle forgot password logic
                  message.info("Forgot password functionality coming soon!");
                }}
              >
                Forgot password?
              </button> */}
            </div>

            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type={showPassword ? "text" : "password"}
                value={passwords.new}
                onChange={(e) => handlePasswordChange("new", e.target.value)}
                className="form-input form-input-gray"
                placeholder="Enter new password"
                required
              />
              <span
                className="job-toggle-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type={showPassword ? "text" : "password"}
                value={passwords.confirm}
                onChange={(e) =>
                  handlePasswordChange("confirm", e.target.value)
                }
                className="form-input form-input-gray"
                placeholder="Confirm new password"
                required
              />
              <span
                className="job-toggle-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              </span>
            </div>

            {passwordError && (
              <div
                className="error-message"
                style={{
                  color: "#ff4d4f",
                  marginTop: "10px",
                  padding: "8px",
                  backgroundColor: "#fff2f0",
                  border: "1px solid #ffccc7",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              >
                {passwordError}
              </div>
            )}

            <button
              type="submit"
              className="submit-btn"
              disabled={passwordLoading}
            >
              {passwordLoading ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>
      </div>
    );
  };

  // Render current screen
  const renderScreen = () => {
    switch (currentScreen) {
      case "edit":
        return <EditProfile />;
      case "password":
        return <ChangePassword />;
      case "profile":
        return <CompanyProfile />;
      default:
        return <CompanyProfile />;
    }
  };

  if (loading)
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <NeonHexagonLoader />
      </div>
    );

  return <div className="app-container">{renderScreen()}</div>;
};

export default ProfileManagement;
